import { useCallback, useEffect, useState } from "react";
import { getCachedImageUrl, getImageUrl, parseVerificationIds } from "../api/imageCache";

const SLOT_COUNT = 2;

const emptySlots = () =>
    Array.from({ length: SLOT_COUNT }, () => ({ key: "", url: "", loading: false }));

// Owns the Front/Back verification ID slots of the guest form.
//
// The slots are re-synced from the guest record whenever a different guest is
// opened, and are deliberately NOT cleared on save: re-opening the guest you
// just edited must still show the IDs it already has, otherwise the next save
// writes an empty verification_id back over them.
export default function useVerificationIdSlots(verificationId, guestKey) {
    const [slots, setSlots] = useState(emptySlots);

    useEffect(() => {
        const ids = parseVerificationIds(verificationId).slice(0, SLOT_COUNT);

        setSlots(
            emptySlots().map((slot, index) => {
                const id = ids[index];
                if (!id) {
                    return slot;
                }
                const cached = getCachedImageUrl(id);
                return { key: id, url: cached || "", loading: !cached };
            }),
        );

        if (ids.length === 0) {
            return undefined;
        }

        let active = true;

        ids.forEach((id, index) => {
            if (getCachedImageUrl(id)) {
                return;
            }
            getImageUrl(id).then((url) => {
                if (!active) {
                    return;
                }
                setSlots((prev) => {
                    if (prev[index]?.key !== id) {
                        return prev;
                    }
                    const next = [...prev];
                    next[index] = { key: id, url: url || "", loading: false };
                    return next;
                });
            });
        });

        return () => {
            active = false;
        };
    }, [verificationId, guestKey]);

    const updateSlot = useCallback((index, patch) => {
        setSlots((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], ...patch };
            return next;
        });
    }, []);

    const setSlotLoading = useCallback(
        (index, loading) => updateSlot(index, { loading }),
        [updateSlot],
    );

    const setSlotImage = useCallback(
        (index, key, url) => updateSlot(index, { key, url: url || "", loading: false }),
        [updateSlot],
    );

    const clearSlot = useCallback(
        (index) => updateSlot(index, { key: "", url: "", loading: false }),
        [updateSlot],
    );

    const resetSlots = useCallback(() => setSlots(emptySlots()), []);

    return {
        slots,
        setSlotLoading,
        setSlotImage,
        clearSlot,
        resetSlots,
        // Joined without the blank slots, so a single ID never serialises as
        // "key," and two blanks never serialise as ",".
        verificationValue: slots.map((slot) => slot.key).filter(Boolean).join(","),
        hasAnyId: slots.some((slot) => slot.key),
        hasFrontId: Boolean(slots[0]?.key),
        hasBackId: Boolean(slots[1]?.key),
        isUploading: slots.some((slot) => slot.loading),
    };
}
